import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import moment from "moment";
import "chartjs-adapter-moment";
import LoadingSpinner from "../common/LoadingSpinner";

import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Title,
  Tooltip,
  Legend,
  CategoryScale,
} from "chart.js";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Title,
  Tooltip,
  Legend,
  CategoryScale
);

const WaterChart = ({ data, report, isSensorActive }) => {
  const [chartData, setChartData] = useState(null);
  const { t } = useTranslation();
  const [ruMax, setRuMax] = useState(0);
  const [ruMin, setRuMin] = useState(0);

  // Stocker la valeur originale du premier point
  useEffect(() => {
    if (!data) return;
    data.forEach((day) => {
      day.bilan.forEach((hourData) => {
        if (hourData.originalValue === undefined) {
          hourData.originalValue = hourData.value;
        }
      });
    });
  }, [data]);

  useEffect(() => {
    if (!data || data.length === 0) return;

    // Copie profonde pour modifier uniquement le premier point du bilan
    const chartInput = JSON.parse(JSON.stringify(data));

    if (chartInput[0]?.bilan?.[0]) {
      chartInput[0].bilan[0].value = isSensorActive
        ? chartInput[0].ruInitial // toggle ON → ruInitial
        : chartInput[0].bilan[0].originalValue; // toggle OFF → valeur originale
    }

    const labels = [];
    const dataBilan = [];
    const ruMaxArr = [];
    const ruMinArr = [];
     const ruabs = [];

     chartInput.forEach((day) => {
      day.bilan.forEach((hourData) => {
        const date = moment(day.dates)
          .add(hourData.hour, "hours")
          .subtract(14, "hours")
          .toISOString();

        labels.push(date);
        dataBilan.push(parseFloat(hourData.value).toFixed(2));
        ruMaxArr.push(day.RUmax);
        ruMinArr.push(day.RUmin);
        ruabs.push(25);
      });
    });

    setChartData({
      labels,
      datasets: [
        {
          label: t("water_balance"),
          data: dataBilan,
          borderColor: "#26a6b7",
          backgroundColor: "rgba(75,192,192,0.3)",
          borderWidth: 2,
          tension: 0,
          fill: true,
        },
        {
          label: t("Ru max (mm)"),
          data: ruMaxArr,
          borderColor: "#32CB8D",
          borderWidth: 1,
          tension: 0,
        },
        {
          label: t("Ru min (mm)"),
          data: ruMinArr,
          borderColor: "#e5331b", 
          borderWidth: 1,
          tension: 0,
        },
      ],
    });

    setRuMax(Math.max(...ruMaxArr));
    setRuMin(Math.min(...ruMinArr));
  }, [data, t, isSensorActive]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: t("water_balance"),
        font: {
          size: 14,
        },
      },
      legend: {
        display: true,
        position: "bottom",
      },
    },
    scales: {
      x: {
        type: "time",
        time: {
          unit: "hour",
          displayFormats: {
            hour: "DD-MM-YYYY HH:mm",
          },
        },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 25,
        },
      },
      y: {
        beginAtZero: false,
        suggestedMin: ruMin - 5,
        suggestedMax: ruMax + 5,
      },
    },
    elements: {
      point: {
        radius: 0,
      },
    },
  };

  if (!chartData) return <LoadingSpinner />;

  return (
    <div className="w-100 d-flex justify-content-center">
      {chartData && (
        <div
          style={{
            width: "100%",
            maxWidth: report ? "448px" : "100%",
            margin: "0 auto",
            backgroundColor: "var(--sf-surface, #ffffff)",
            padding: "10px",
            borderRadius: "8px",
          }}
        >
          <div style={{ height: report ? "324px" : "300px" }}>
            <Line data={chartData} options={options} />
          </div>
        </div>
      )}
    </div>
  );
};

export default WaterChart;